import moment from 'moment';

/**
 * Convert a STORED date value into the display string a vue2-datepicker widget
 * (value-type="format") expects — a string in `format` (e.g. "MM/DD/YYYY").
 *
 *  - empty (null / undefined / '' / whitespace-only)  -> null
 *      Widget renders BLANK — never the literal "Invalid date", and never
 *      moment()'s fallback to *today's* date for `undefined`. This matters now
 *      that DatePickerControl applies its value on mount (immediate watcher):
 *      controls mount before their data loads with an undefined value.
 *  - already-formatted "MM/DD/YYYY" string -> passthrough (no moment re-parse,
 *      so no UTC drift when rendered headless in Lambda/Puppeteer PDF export).
 *  - ISO string / Date / moment / anything parseable -> moment(value).format(...).
 *  - unparseable garbage -> null (blank), NOT the literal "Invalid date".
 *
 * Fixes "Policy Holder's Birth Date not staying" (Smile Bar, 2026-07-01): birth
 * dates are stored as ISO ("1982-06-10T04:00:00.000Z") and must render, while an
 * unset (or malformed) date must stay blank.
 *
 * KEEP IN SYNC: cypress/node-tests/format-date-display.test.js loads this file.
 */
export default function formatDateForDisplay(value, format, returnType) {
    // Empty (including whitespace-only) -> blank.
    if (value === null || value === undefined) {
        return null;
    }
    if (typeof value === 'string' && value.trim() === '') {
        return null;
    }

    // Already a formatted date string (MM/DD/YYYY) — use as-is to avoid a moment
    // re-parse that would shift the day in UTC environments (Lambda/Puppeteer PDF).
    if (typeof value === 'string' && value.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
        return value;
    }

    const fmt = (returnType === 'format' && format) ? format : 'MM/DD/YYYY';
    const m = moment(value);

    // A malformed value renders blank — never the literal "Invalid date".
    return m.isValid() ? m.format(fmt) : null;
}
